import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, Bot, Check, ChevronDown, ChevronRight, Copy, FilePlus2, FileText,
  FolderTree, GitMerge, Hash, Inbox, Loader2, Mail, MessageCircleWarning, MessageSquareText, Mic, Paperclip, Pencil, Quote, Scissors, Sparkles, Terminal, Trash2,
  Upload, Wrench, X, Zap,
} from 'lucide-react';
import { CitationMarginCard, CitationPanel } from '../components/kb/CitationPanel';
import { CitationSourceDrawer } from '../components/kb/CitationSourceDrawer';
import { RunPhaseHeader, RunTimelineView } from '../components/run/RunActivity';
import { ReviewQueue } from '../components/review/ReviewQueue';
import { localMidnight } from '../components/review/review-model';
import { RunReportPanel } from '../components/run/RunReport';
import { RunFeedback } from '../components/run/RunFeedback';
import { RunCostChip, RunCostStats, describeRunUsage, runUsageOf } from '../components/run/RunCost';
import { PartText } from '../components/run/PartText';
import { PlacementMark, RunPlacement } from '../components/run/RunPlacement';
import { Button } from '../components/ui/button';
import { Input, Textarea } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { BreathingDot, SweepSurface } from '../components/ui/breathing';
import { Dialog } from '../components/ui/dialog';
import { Drawer } from '../components/ui/drawer';
import { LineDiffView, type DiffAnnotation, type DiffLineTarget, type DiffMarginNote } from '../components/ui/diff';
import {
  api, type FeedSourceType, type KbChange, type KbChangeComment, type KbChangeKind, type KbCitation, type KbNodeCitations,
  type KbReviewStatus, type KbRun, type KbRunDetail, type KbRunItem, type KbRunStatus, type KbRunStep,
  type ProjectScopeLevel,
} from '../lib/api';
import { formatRunCost, guardrailCategoryLabel } from '@repo/shared-types';
import { ProjectTag, ScopeBar } from '../components/scope/ScopeBar';
import { useAuth } from '../lib/auth';
import { useScope } from '../lib/scope';
import { formatRunLabel, formatSource, useSourceLabels } from '../lib/source-label';
import { useProjectTree } from '../lib/project-tree';
import { useLiveHeadline, useRunStream } from '../lib/run-stream';
import { formatChars, isRunLive } from '../lib/runs';
import { cn, formatDate, timeAgo } from '../lib/utils';

const POLL_MS = 4_000;

/**
 * One page of runs. The tab loads this many and then keeps going as the list is scrolled — a page
 * deeper than the viewport on purpose, since one shorter than a screen would fetch two or three
 * times before the reader had scrolled at all.
 */
const RUNS_PAGE = 50;

/**
 * Folds a freshly fetched page of runs into what is already on screen, newest first.
 *
 * Merged rather than appended, because both of the things that fetch runs here can hand back rows
 * the list already holds: the poll re-reads the *first* page, whose rows have moved on (a status, a
 * phase, a cost), and a scroll fetches by offset, which shifts under it when a new run arrives at
 * the top between the request and the one before it. Keyed by id so neither case can double a row,
 * with the newer copy winning — which is what lets one function serve the poll and the paging.
 */
function mergeRuns(prev: KbRun[], page: KbRun[]): KbRun[] {
  const byId = new Map(prev.map((run) => [run.id, run]));
  for (const run of page) byId.set(run.id, run);
  return [...byId.values()].sort((a, b) => {
    const byTime = new Date(b.queuedAt).getTime() - new Date(a.queuedAt).getTime();
    // Ties are real — a batch window dispatches a run per destination in the same instant — and
    // without a second key the merged order would differ from the last one for no reason, swapping
    // two rows under the reader on every poll.
    return byTime !== 0 ? byTime : a.id < b.id ? 1 : -1;
  });
}

const KIND_META: Record<KbChangeKind, { icon: typeof FileText; label: string; tone: string }> = {
  CREATE: { icon: FilePlus2, label: 'New node', tone: 'text-green-600 dark:text-green-400' },
  UPDATE: { icon: FileText, label: 'Amended', tone: 'text-orange-600 dark:text-orange-400' },
  DELETE: { icon: Trash2, label: 'Removed', tone: 'text-red-600 dark:text-red-400' },
  MERGE: { icon: GitMerge, label: 'Merged', tone: 'text-purple-600 dark:text-purple-400' },
  MOVE: { icon: ArrowRight, label: 'Moved', tone: 'text-blue-600 dark:text-blue-400' },
};

/**
 * How confident the agent was, as a chip — or nothing at all.
 *
 * The `KB_REVIEW_CONFIDENCE` capability is enforced *here* rather than at each call site: this is the
 * only thing on the page that shows the number, and gating the component means a future third place
 * that renders one cannot forget to ask. Presentation only — `KbChange.confidence` still arrives from
 * the API, so this hides a control rather than withholding a secret.
 */
function ConfidenceChip({ value }: { value: number }) {
  const { hasCapability } = useAuth();
  if (!hasCapability('KB_REVIEW_CONFIDENCE')) return null;

  const pct = Math.round(value * 100);
  const tone =
    value >= 0.9
      ? 'bg-green-100 dark:bg-green-950/50 text-green-700 dark:text-green-400'
      : value >= 0.7
        ? 'bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400'
        : 'bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400';
  return <span className={cn('px-1.5 py-0.5 rounded-full text-[10px] font-semibold', tone)}>{pct}% confident</span>;
}

/**
 * Where the changed page sits in its project — or nothing at all.
 *
 * `KB_REVIEW_NODE_PATH` is enforced here rather than at the two call sites, for the reason
 * `ConfidenceChip` above is: these are the only places this page prints a slug, and gating the
 * component means a third one added later cannot forget to ask. Presentation only — `KbChange.path`
 * still arrives from the API and both rows still navigate by it.
 *
 * The two variants are the rows they sit in, not a preference: the card gives the path its own line
 * under the title and spells a MOVE out as `from → to`, while the run's change list has one narrow
 * row and has always shown the destination alone. Left exactly as they were — this adds a switch,
 * it does not restyle either one.
 */
function NodePath({ change, variant }: { change: KbChange; variant: 'card' | 'inline' }) {
  const { hasCapability } = useAuth();
  if (!hasCapability('KB_REVIEW_NODE_PATH')) return null;

  if (variant === 'inline') {
    return <span className="text-[10px] font-mono text-ink-faint truncate">{change.path}</span>;
  }
  return (
    <p className="text-[11px] font-mono text-ink-faint truncate mt-0.5">
      {change.kind === 'MOVE' && change.fromPath ? `${change.fromPath} → ${change.path}` : change.path}
    </p>
  );
}

/** How each kind reads inside "under the N% floor for …". Keyed by the *floor* kind, which can
 * differ from the row's own: a merge's losing node is a DELETE row judged by the MERGE floor. */
const FLOOR_KIND_LABEL: Record<KbChangeKind, string> = {
  CREATE: 'new pages',
  UPDATE: 'edits',
  DELETE: 'removals',
  MERGE: 'merges',
  MOVE: 'moves',
};

/**
 * Why this change is in the queue at all.
 *
 * Stated from the row itself — `reviewFloor`/`reviewFloorKind` are stamped at commit time — because
 * floors differ by kind and are env-tunable, so the confidence number alone stops explaining
 * anything once a floor has been retuned. Rows from before the columns existed say nothing rather
 * than guessing. The numeric wording respects `KB_REVIEW_CONFIDENCE` the same way the chip does;
 * without the capability the sentence still names the floor that was missed, just not the scores.
 */
function FlagReason({ change }: { change: KbChange }) {
  const { hasCapability } = useAuth();
  // NOT_REQUIRED never entered the queue; every other status means this row was flagged.
  if (change.reviewStatus === 'NOT_REQUIRED' || change.reviewFloor == null) return null;
  // Until 2026-10-03 a fix run's changes were flagged whatever their confidence, stamped with a floor
  // of 1, so the floor sentence below would read as "the agent was unsure" when it was not. Those rows
  // still exist; a fix change since then is judged like any other and gets the ordinary sentence.
  if (change.run?.scopePath && change.reviewFloor >= 1) {
    return (
      <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-2">
        From a fix run a reviewer started on {change.run.scopePath} — at the time, every change a fix
        run made was held for that reviewer to confirm the correction was applied as they meant it.
      </p>
    );
  }  const kind = FLOOR_KIND_LABEL[change.reviewFloorKind ?? change.kind];
  return (
    <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-2">
      {hasCapability('KB_REVIEW_CONFIDENCE')
        ? `Flagged for review: the agent was ${Math.round(change.confidence * 100)}% confident, under the ${Math.round(change.reviewFloor * 100)}% floor for ${kind}.`
        : `Flagged for review: the agent's confidence fell under the floor for ${kind}.`}
    </p>
  );
}

/** One pinned comment, rendered beneath its quoted line inside the diff. */
function ReviewCommentNote({ comment, onDelete }: { comment: KbChangeComment; onDelete?: () => void }) {
  return (
    <div className="flex items-start gap-2">
      <MessageCircleWarning
        size={14}
        className={cn('mt-0.5 flex-shrink-0', comment.status === 'DRAFT' ? 'text-amber-600 dark:text-amber-500' : 'text-ink-faint')}
      />
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] leading-relaxed text-ink-secondary whitespace-pre-wrap">{comment.text}</p>
        <p className="text-[11px] text-ink-faint mt-0.5">
          {comment.status === 'DRAFT' ? 'Draft — goes into the next fix run' : 'Submitted to a fix run'}
          {comment.onRemoved ? ' · on text this change removed' : ''}
        </p>
      </div>
      {onDelete && (
        <button onClick={onDelete} title="Discard this draft" className="text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors flex-shrink-0">
          <Trash2 size={11} />
        </button>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: KbReviewStatus }) {
  switch (status) {
    case 'PENDING':
      return <Badge variant="warning">Awaiting review</Badge>;
    case 'ACCEPTED':
      return <Badge variant="success">Accepted</Badge>;
    case 'REJECTED':
      return <Badge variant="danger">Rejected · reverted</Badge>;
    case 'KEPT_BOTH':
      return <Badge variant="info">Kept both</Badge>;
    case 'NOT_REQUIRED':
      return <Badge>Auto-applied</Badge>;
    case 'SUPERSEDED':
      return <Badge variant="info">Superseded · fixed by a later run</Badge>;
  }
}

/**
 * One reviewable change.
 *
 * The diff is rendered from the change's own before/after snapshots rather than the node's
 * current state, so a reviewer always sees the edit that was actually made — even if a later
 * ingestion run has since touched the same node.
 */
function ChangeCard({
  change, projectId, onResolved, onOpenNode, evidenceRail = true,
}: {
  change: KbChange;
  /**
   * The project the *request* is scoped to, not the one the change belongs to. Every change endpoint
   * resolves the caller's scope server-side, so a parent can act on a descendant's change — which is
   * the whole point of a queue that spans projects.
   */
  projectId: string;
  onResolved: (message: string, changedProjectId: string) => void;
  onOpenNode: (path: string, changedProjectId: string) => void;
  /**
   * Whether to reserve the evidence rail beside the card. Off inside the run drawer, which is too
   * narrow to give a column away; the citations then stay in the panel beneath the diff.
   */
  evidenceRail?: boolean;
}) {
  const [open, setOpen] = useState(change.reviewStatus === 'PENDING');
  const [busy, setBusy] = useState<null | 'accept' | 'reject' | 'keepBoth' | 'edit' | 'feedback'>(null);
  const [error, setError] = useState<string | null>(null);
  const [citations, setCitations] = useState<KbNodeCitations | null>(null);
  const [selectedCitation, setSelectedCitation] = useState<KbCitation | null>(null);
  /** The inline page editor; the fix flow lives in the Apply-feedback popup instead. */
  const [intervene, setIntervene] = useState<null | 'edit'>(null);
  const [confirmFix, setConfirmFix] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editNote, setEditNote] = useState('');
  const [feedback, setFeedback] = useState('');
  /** Set once a fix run is queued — the change stays open, so the card says what is happening. */
  const [fixRun, setFixRun] = useState<{ runId: string; message: string } | null>(null);
  /**
   * The fix run the row already carries (`fixRunId`), fetched on expand — a fix started before this
   * page load must still show, still link, and still block a second fix while it is in flight. The
   * happy path never reaches this: a fix that commits supersedes the whole card.
   */
  const [linkedFix, setLinkedFix] = useState<{ status: KbRunStatus } | null>(null);
  /** The review being drafted: comments pinned to diff lines, submitted together as one fix run. */
  const [comments, setComments] = useState<KbChangeComment[] | null>(null);
  const [composer, setComposer] = useState<DiffLineTarget | null>(null);
  const [commentText, setCommentText] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);
  /**
   * The evidence rail beside this card, which the diff portals its margin notes into.
   *
   * State rather than a ref: the diff can only portal once the node exists, and a ref's mutation
   * schedules no render — the margin would stay empty until something else happened to re-render.
   */
  const [rail, setRail] = useState<HTMLDivElement | null>(null);

  // Loaded on first expand rather than with the list: evidence is only worth a request once
  // someone is actually looking at the change.
  useEffect(() => {
    if (!open || citations) return;
    api.kb
      .changeDetail(projectId, change.id)
      .then((d) => setCitations(d.citations))
      .catch(() => setCitations({ current: [], superseded: [] }));
  }, [open, citations, projectId, change.id]);

  // Same lazy pattern for a linked fix run: one status read on expand says whether feedback is
  // already being worked, which is what gates offering to send more.
  useEffect(() => {
    if (!open || !change.fixRunId || change.reviewStatus !== 'PENDING' || fixRun || linkedFix) return;
    api.kb
      .run(change.projectId, change.fixRunId)
      .then((run) => setLinkedFix({ status: run.status }))
      .catch(() => {});
  }, [open, change.fixRunId, change.reviewStatus, change.projectId, fixRun, linkedFix]);

  // The drafted review, loaded with the card: comments must survive a reload, so they live on the
  // server from the moment they are written.
  useEffect(() => {
    if (!open || comments) return;
    api.kb
      .changeComments(projectId, change.id)
      .then(setComments)
      .catch(() => setComments([]));
  }, [open, comments, projectId, change.id]);

  const meta = KIND_META[change.kind];
  const Icon = meta.icon;
  const isPending = change.reviewStatus === 'PENDING';
  // Keep-both splits replaced content in two, so it only means anything for the kinds that
  // replaced something. The server enforces this too.
  const canKeepBoth = change.kind === 'UPDATE' || change.kind === 'MERGE';

  const act = async (which: 'accept' | 'reject' | 'keepBoth') => {
    setBusy(which);
    setError(null);
    try {
      const result = await api.kb[which](projectId, change.id);
      onResolved(result.message, change.projectId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  /** Seeds the inline editor with the page as it stands now — not the change's snapshot, which a
      later edit may already have moved past. */
  const openEditor = async () => {
    setIntervene('edit');
    setError(null);
    setEditorLoading(true);
    try {
      const node = await api.kb.node(change.projectId, change.path);
      setEditTitle(node.title);
      setEditBody(node.body);
    } catch (e: any) {
      setError(e.message);
      setIntervene(null);
    } finally {
      setEditorLoading(false);
    }
  };

  const saveEdit = async () => {
    setBusy('edit');
    setError(null);
    try {
      const result = await api.kb.resolveChangeByEdit(projectId, change.id, {
        title: editTitle,
        body: editBody,
        ...(editNote.trim() ? { note: editNote.trim() } : {}),
      });
      onResolved(result.message, change.projectId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const drafts = (comments ?? []).filter((c) => c.status === 'DRAFT');

  const startFix = async () => {
    setBusy('feedback');
    setError(null);
    try {
      const result = await api.kb.fixChange(projectId, change.id, feedback.trim() || undefined);
      if (result.skippedDuplicate) {
        setError(result.message);
      } else {
        setFixRun(result);
        setIntervene(null);
        setConfirmFix(false);
        setFeedback('');
        // The drafts just became part of the run's record; show them as such without refetching.
        setComments((prev) =>
          (prev ?? []).map((c) => (c.status === 'DRAFT' ? { ...c, status: 'SUBMITTED' as const, fixRunId: result.runId } : c)),
        );
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const addComment = async () => {
    if (!composer || commentText.trim().length < 3) return;
    setCommentBusy(true);
    setError(null);
    try {
      const created = await api.kb.addChangeComment(projectId, change.id, {
        text: commentText.trim(),
        quote: composer.quote,
        onRemoved: composer.removed,
      });
      setComments((prev) => [...(prev ?? []), created]);
      setComposer(null);
      setCommentText('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCommentBusy(false);
    }
  };

  const removeComment = async (commentId: string) => {
    try {
      await api.kb.deleteChangeComment(projectId, change.id, commentId);
      setComments((prev) => (prev ?? []).filter((c) => c.id !== commentId));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const hasBodyDiff = Boolean(change.beforeBody || change.afterBody);

  /** Whether the diff — and so the margin evidence hangs beside it — is what this card is showing. */
  const showingDiff = intervene !== 'edit' && change.kind !== 'MOVE' && hasBodyDiff;

  /**
   * Evidence as margin notes, anchored on each citation's `nodeQuote` — the verbatim span of the
   * body the claim is evidence for, which is exactly what the margin needs to find its line.
   *
   * Only the current ones: a superseded citation quotes text this page no longer says, so it has no
   * anchor in the body by definition and stays in the panel below with the rest of the history.
   */
  const marginEvidence: DiffMarginNote[] = useMemo(
    () =>
      showingDiff
        ? (citations?.current ?? [])
            .filter((c) => c.nodeQuote)
            .map((c) => ({
              key: c.id,
              quote: c.nodeQuote,
              node: <CitationMarginCard citation={c} onOpen={setSelectedCitation} />,
            }))
        : [],
    [showingDiff, citations],
  );

  /**
   * Whether the rail is actually carrying the evidence — which decides whether the panel below the
   * diff should stand down.
   *
   * `rail` and not just `marginEvidence`: with no host there is no margin, and keying the panel on
   * the notes alone would hide the current citations in the run drawer, where the rail is off. The
   * first render after expanding has `rail` still null, but the citations are fetched on expand and
   * so are null too — nothing renders either way until both have arrived.
   */
  const evidenceInRail = rail !== null && marginEvidence.length > 0;

  /** A fix run is rewriting this page right now — started here, or found on the row. */
  const fixInFlight =
    Boolean(fixRun) || Boolean(linkedFix && (linkedFix.status === 'QUEUED' || linkedFix.status === 'PROCESSING'));

  /** Whether the review is still being drafted: pending, page present, no fix already in flight. */
  const canReview = isPending && change.kind !== 'DELETE' && !fixInFlight;

  // Comments render inside the diff, beneath the lines they quote — the composer too, so writing a
  // comment happens where the line is, GitHub-style. Whatever no longer matches a line falls to the
  // diff's own footer rather than vanishing.
  const annotations: DiffAnnotation[] = [
    ...(comments ?? [])
      .filter((c) => c.quote)
      .map((c) => ({
        key: c.id,
        quote: c.quote!,
        removed: c.onRemoved,
        node: (
          <ReviewCommentNote
            key={c.id}
            comment={c}
            onDelete={canReview && c.status === 'DRAFT' ? () => removeComment(c.id) : undefined}
          />
        ),
      })),
    ...(composer
      ? [
          {
            key: 'composer',
            quote: composer.quote,
            removed: composer.removed,
            node: (
              <div key="composer" className="space-y-1.5">
                <Textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={2}
                  autoFocus
                  className="text-[12.5px]"
                  placeholder="What's wrong here, or what should it say?"
                />
                <div className="flex gap-2 justify-end">
                  <Button disabled={commentBusy} onClick={() => { setComposer(null); setCommentText(''); }}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    disabled={commentBusy || commentText.trim().length < 3}
                    onClick={addComment}
                  >
                    {commentBusy ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Check size={12} className="mr-1.5" />}
                    Add comment
                  </Button>
                </div>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    // The card and its evidence rail, side by side. The rail is deliberately *outside* the card's
    // border: evidence is a marginal note about the edit, not part of it, and inside the card it
    // was competing with the diff for the same padded width. The column is reserved on every card,
    // filled or not, so a queue of cards keeps one straight edge — the same reason a document with
    // one comment still leaves its margin where it is.
    <div className="flex flex-col gap-4 md:flex-row md:items-start">
      <div className={cn('min-w-0 flex-1 border rounded-lg overflow-hidden bg-card', isPending ? 'border-amber-300 dark:border-amber-900' : 'border-border')}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-surface-subtle transition-colors">
        {open ? <ChevronDown size={13} className="text-ink-faint mt-1 flex-shrink-0" /> : <ChevronRight size={13} className="text-ink-faint mt-1 flex-shrink-0" />}
        <Icon size={14} className={cn('mt-0.5 flex-shrink-0', meta.tone)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn('text-[11px] font-semibold uppercase tracking-wide', meta.tone)}>{meta.label}</span>
            <span className="text-xs font-medium text-ink truncate">{change.afterTitle ?? change.beforeTitle ?? change.path}</span>
            {/* Which level this happened at. Paths are project-relative, so in a queue spanning
                several projects the path alone doesn't say whose page changed. */}
            <ProjectTag name={change.projectName} />
          </div>
          <NodePath change={change} variant="card" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <ConfidenceChip value={change.confidence} />
          <StatusBadge status={change.reviewStatus} />
        </div>
      </button>

      {open && (
        <div className="border-t border-border-subtle">
          {/* Agent reasoning */}
          <div className="px-4 py-3 bg-surface-subtle border-b border-border-subtle">
            <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em] mb-1 flex items-center gap-1">
              <Sparkles size={9} />Why the agent did this
            </p>
            <p className="text-xs text-ink-secondary leading-relaxed">{change.reasoning}</p>
            <FlagReason change={change} />
            <div className="flex items-center gap-3 mt-2 text-[10px] text-ink-faint flex-wrap">
              <span>{formatDate(change.createdAt)}</span>
              {change.model && <span className="font-mono">{change.model}</span>}
              {change.mergedFromPaths.length > 0 && (
                <span>merged from {change.mergedFromPaths.join(', ')}</span>
              )}
              {change.kind === 'DELETE' && change.fromPath && (
                <span>folded into {change.fromPath}</span>
              )}
              <button
                onClick={() => onOpenNode(change.path, change.projectId)}
                className="text-orange-500 hover:text-orange-700 transition-colors"
              >
                Open node
              </button>
            </div>
          </div>

          {/* Diff — or, while editing, the page itself where the diff was: the reviewer rewrites
              the original text in place rather than in a panel below the verdicts. */}
          <div className="px-4 py-3">
            {intervene === 'edit' ? (
              <div className="space-y-2">
                {editorLoading ? (
                  <p className="text-[11px] text-ink-faint flex items-center gap-1.5">
                    <Loader2 size={11} className="animate-spin" />Loading the current page…
                  </p>
                ) : (
                  <>
                    <p className="text-[11px] text-ink-faint">
                      You are editing the page as it stands now. Saving applies your edit, records it as
                      its own source, and resolves this change.
                    </p>
                    <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Title" />
                    <Textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={18}
                      className="font-mono text-[12.5px]"
                      placeholder="Markdown body…"
                    />
                    <Input
                      value={editNote}
                      onChange={(e) => setEditNote(e.target.value)}
                      placeholder="Why? (optional — recorded with your edit's evidence)"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button disabled={busy !== null} onClick={() => setIntervene(null)}>Cancel</Button>
                      <Button variant="primary" disabled={busy !== null || !editBody.trim()} onClick={saveEdit}>
                        {busy === 'edit' ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Check size={12} className="mr-1.5" />}
                        Save & resolve
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ) : change.kind === 'MOVE' ? (
              <p className="text-xs text-ink-secondary">
                Re-filed from <span className="font-mono text-ink">{change.fromPath}</span> to{' '}
                <span className="font-mono text-ink">{change.path}</span>
                {change.beforeTitle !== change.afterTitle && (
                  <> and renamed from "{change.beforeTitle}" to "{change.afterTitle}"</>
                )}
                . Body unchanged.
              </p>
            ) : hasBodyDiff ? (
              <LineDiffView
                oldText={change.beforeBody ?? ''}
                newText={change.afterBody ?? ''}
                annotations={annotations}
                margin={marginEvidence}
                marginHost={rail}
                onCommentLine={
                  canReview ? (target) => { setComposer(target); setCommentText(''); setError(null); } : undefined
                }
              />
            ) : (
              <p className="text-xs text-ink-faint">No body content recorded for this change.</p>
            )}

          </div>

          {/* The evidence behind this specific edit — what a reviewer needs in order to judge the
              diff on anything better than plausibility.

              With a diff on screen the current citations are in its margin, level with the lines
              they back, and what is left here is the superseded group: text this page no longer
              says, which has nowhere to anchor. `CitationPanel` renders nothing at all when that
              group is empty, so this whole block disappears on the common case. */}
          {citations && (evidenceInRail ? citations.superseded.length > 0 : citations.current.length > 0) && (
            <div className="px-4 pb-3">
              <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em] mb-2 flex items-center gap-1">
                <Quote size={9} />
                {evidenceInRail ? 'Earlier evidence' : 'Evidence for this change'}
              </p>
              <CitationPanel
                citations={citations}
                onOpen={setSelectedCitation}
                currentShownElsewhere={evidenceInRail}
              />
            </div>
          )}

          {error && (
            <p className="mx-4 mb-3 text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-1.5">{error}</p>
          )}

          {/* Actions */}
          {isPending ? (
            <div className="border-t border-border-subtle bg-surface-subtle">
              <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[11px] text-ink-faint">
                  {fixInFlight
                    ? 'A fix run is rewriting this page — the verdicts unlock when it settles.'
                    : canReview
                      ? 'This change is already live. Hover a diff line and click + to pin feedback, then apply it.'
                      : 'This change is already live. Accepting confirms it; rejecting reverts it.'}
                </p>
                <div className="flex gap-1.5 flex-shrink-0 flex-wrap">
                  {canReview && (
                    <Button disabled={busy !== null} onClick={openEditor}>
                      <Pencil size={12} className="mr-1.5" />
                      Edit
                    </Button>
                  )}
                  {canReview && drafts.length > 0 && (
                    <Button
                      disabled={busy !== null}
                      onClick={() => { setConfirmFix(true); setError(null); }}
                      className="border-orange-300/80 dark:border-orange-800 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/40"
                    >
                      <Wrench size={12} className="mr-1.5" />
                      Apply feedback
                      <span className="ml-1 px-1.5 py-px rounded-full bg-orange-500 text-white text-[10px] font-semibold tabular-nums">
                        {drafts.length}
                      </span>
                    </Button>
                  )}
                  <Button variant="danger" disabled={busy !== null || fixInFlight} onClick={() => act('reject')}>
                    {busy === 'reject' ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <X size={12} className="mr-1.5" />}
                    Reject
                  </Button>
                  {canKeepBoth && (
                    <Button disabled={busy !== null || fixInFlight} onClick={() => act('keepBoth')}>
                      {busy === 'keepBoth' ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Copy size={12} className="mr-1.5" />}
                      Keep both
                    </Button>
                  )}
                  <Button variant="primary" disabled={busy !== null || fixInFlight} onClick={() => act('accept')}>
                    {busy === 'accept' ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Check size={12} className="mr-1.5" />}
                    Accept
                  </Button>
                </div>
              </div>

              {/* Direct intervention: the two answers between "confirm" and "revert" — rewrite the
                  page yourself, or state the correction and let the agent apply it within this page.
                  Only for changes whose page still exists; a DELETE's page is gone, and reject is
                  the restore. */}
              {change.kind !== 'DELETE' && (
                <div className="px-4 pb-3">
                  {fixInFlight ? (
                    <div className="flex items-center gap-2 text-[11px] bg-surface border border-border-subtle rounded px-3 py-2">
                      <Wrench size={11} className="text-orange-500 flex-shrink-0" />
                      <span className="text-ink-secondary flex-1">
                        {fixRun?.message ??
                          'A fix run is rewriting this page from earlier feedback. When it lands, this change is superseded; the fix’s own changes come back here only if they need a look.'}
                      </span>
                      <Link
                        to={`/activity?run=${fixRun?.runId ?? change.fixRunId}`}
                        className="text-orange-500 hover:text-orange-700 transition-colors flex-shrink-0"
                      >
                        Watch the run
                      </Link>
                    </div>
                  ) : linkedFix && change.fixRunId ? (
                    /* A settled fix run that left this change open means the feedback did not
                       land: the run failed, or it committed nothing. Said plainly, because a
                       reviewer who cannot see this concludes their feedback was ignored. */
                    <div className="flex items-center gap-2 text-[11px] bg-surface border border-border-subtle rounded px-3 py-2">
                      <Wrench size={11} className="text-ink-faint flex-shrink-0" />
                      <span className="text-ink-secondary flex-1">
                        {linkedFix.status === 'FAILED'
                          ? 'An earlier fix run failed before changing the page. The feedback can be sent again.'
                          : 'An earlier fix run finished without changing the page — the agent found nothing it could apply. Adjust the feedback, or resolve the change yourself.'}
                      </span>
                      <Link
                        to={`/activity?run=${change.fixRunId}`}
                        className="text-orange-500 hover:text-orange-700 transition-colors flex-shrink-0"
                      >
                        See the run
                      </Link>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ) : (
            change.reviewedAt && (
              <div className="px-4 py-2 border-t border-border-subtle bg-surface-subtle text-[11px] text-ink-faint">
                Resolved {formatDate(change.reviewedAt)}
                {change.reviewNote && ` — ${change.reviewNote}`}
              </div>
            )
          )}
        </div>
      )}

      <CitationSourceDrawer
        citation={selectedCitation}
        projectId={projectId}
        onClose={() => setSelectedCitation(null)}
      />

      {/* The "are you sure" for a review submission: what applying feedback means, how many
          comments go, and an optional final note — the last chance to add the overall thought. */}
      <Dialog open={confirmFix} onClose={() => setConfirmFix(false)} title="Apply feedback?">
        <div className="space-y-3">
          <p className="text-xs text-ink-secondary leading-relaxed">
            Your feedback becomes a dated source and a run confined to this page — the agent applies
            the correction and cites your words. Pin comments to specific lines by hovering them in
            the diff; they are all submitted together.
          </p>
          <p className="text-[11px] text-ink-faint">
            {drafts.length} pinned comment{drafts.length === 1 ? '' : 's'} will be submitted. When the
            run lands, this change is marked superseded; the fix's own changes come back to this queue
            only if they conflict, hold sensitive information, or the agent was unsure.
          </p>
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            placeholder="Final notes (optional)"
          />
          {error && (
            <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-1.5">{error}</p>
          )}
          <div className="flex gap-2 justify-end">
            <Button disabled={busy !== null} onClick={() => setConfirmFix(false)}>Cancel</Button>
            <Button variant="primary" disabled={busy !== null} onClick={startFix}>
              {busy === 'feedback' ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Wrench size={12} className="mr-1.5" />}
              Start fix run
            </Button>
          </div>
        </div>
      </Dialog>
      </div>

      {/* The rail the diff portals its evidence into. `relative` because the notes are positioned
          against it, and it must start level with the card for those offsets to mean anything —
          hence `items-start` on the row above and no top margin here. Empty until a diff resolves
          a citation to one of its lines. */}
      {evidenceRail && <div ref={setRail} className="relative w-full md:w-[248px] md:flex-shrink-0" />}
    </div>
  );
}

// ─── Runs ───────────────────────────────────────────────────────────────────

/**
 * A run's state as one glyph.
 *
 * A live run breathes rather than spins. The distinction is not decoration: a spinner is the same
 * whether a run is eight seconds or eight minutes from done, whereas the pulse reads as "this is
 * alive" — which, for a queue where one agent loop takes a minute, is the actual question.
 */
function RunStatusIcon({ status, size = 12 }: { status: KbRunStatus; size?: number }) {
  if (isRunLive(status)) return <BreathingDot size={size - 3} />;
  if (status === 'FAILED') return <X size={size} className="text-red-500" />;
  return <Zap size={size} className="text-green-500" />;
}

/**
 * What a run did — or, while it is still going, what it is doing.
 *
 * The live half comes from `KbRun.activity`, which the worker keeps current on the row itself, so
 * every visible run says something specific under a poll rather than needing a stream each.
 */
function runTally(run: KbRun): string {
  if (isRunLive(run.status)) return run.activity ?? run.status.toLowerCase();
  if (run.status !== 'COMPLETED') return run.status.toLowerCase();
  if (run.result?.skippedDuplicate) return 'skipped — already ingested';
  // The report's own line, once the run has one: what happened to what was sent, not page counts.
  if (run.result?.report?.summary) return run.result.report.summary;
  const flagged = run.flaggedForReview ? ` · ${run.flaggedForReview} flagged` : '';
  return `+${run.nodesCreated} ~${run.nodesUpdated} −${run.nodesDeleted}${flagged}`;
}

function RunRow({ run, projectName, onOpen }: { run: KbRun; projectName: string | null; onOpen: () => void }) {
  const live = isRunLive(run.status);
  const sourceLabels = useSourceLabels();
  return (
    <SweepSurface active={live}>
      <button
        onClick={onOpen}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left hover:bg-surface-subtle transition-colors"
      >
        <RunStatusIcon status={run.status} />
        <span className="text-xs font-medium text-ink truncate max-w-[16rem]">
          {formatRunLabel(run.fileName, sourceLabels) || formatSource(run.source, sourceLabels)}
        </span>
        <RunPlacement run={run} projectName={projectName} />
        <span className={cn('text-[11px] truncate', live ? 'text-orange-600 dark:text-orange-400' : 'text-ink-faint')}>
          {runTally(run)}
        </span>
        {run.errorMessage && <span className="text-[11px] text-danger-text truncate">{run.errorMessage}</span>}
        <span className="ml-auto flex items-center gap-2 flex-shrink-0">
          {run.toolCalls > 0 && (
            <span className="text-[10px] text-ink-faint inline-flex items-center gap-1">
              <Wrench size={9} />
              {run.toolCalls}
            </span>
          )}
          {/* Next to the step count rather than in the drawer only: cost per run means something
              read down a column — one run that cost ten times its neighbours is the thing worth
              noticing, and nobody opens twenty drawers to notice it. */}
          <RunCostChip usage={runUsageOf(run)} />
          <span className="text-[11px] text-ink-faint whitespace-nowrap">{timeAgo(run.queuedAt)}</span>
        </span>
      </button>
    </SweepSurface>
  );
}

/**
 * The agent's own account of the run: every tool call in order, with its input and output.
 *
 * Same component shape as the automation run trail, because it answers the same question — but
 * with chunk headers, since a long source is absorbed by several sequential loops and a step's
 * meaning depends on which one it belonged to.
 */
function StepTrail({ steps }: { steps: KbRunStep[] }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const multiChunk = steps.some((s) => s.chunk > 1);

  return (
    <div className="divide-y divide-border-subtle border border-border rounded-lg overflow-hidden">
      {steps.map((step, i) => (
        <div key={i}>
          {multiChunk && step.chunk !== steps[i - 1]?.chunk && (
            <p className="px-3 py-1 bg-surface-muted text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint">
              Chunk {step.chunk}
            </p>
          )}
          <button
            onClick={() => setExpanded(expanded === i ? null : i)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-subtle transition-colors"
          >
            <span className="text-[10px] tabular-nums text-ink-faint w-5">{i + 1}</span>
            <Wrench size={11} className="text-ink-faint flex-shrink-0" />
            <span
              className={cn(
                'text-xs font-mono',
                // A refusal is the interesting half of the trail — where the loop corrected itself.
                step.tool.startsWith('stage_rejected') || step.tool === 'loop_error'
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-ink-secondary',
              )}
            >
              {step.tool}
            </span>
            {expanded === i ? (
              <ChevronDown size={12} className="ml-auto text-ink-faint" />
            ) : (
              <ChevronRight size={12} className="ml-auto text-ink-faint" />
            )}
          </button>
          {expanded === i && (
            <div className="px-3 pb-3 space-y-2 bg-surface-subtle">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-ink-faint mb-1">Input</p>
                <pre className="text-[11px] font-mono text-ink-secondary whitespace-pre-wrap break-words">
                  {JSON.stringify(step.input, null, 2)}
                </pre>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-ink-faint mb-1">Output</p>
                <pre className="text-[11px] font-mono text-ink-secondary whitespace-pre-wrap break-words">
                  {JSON.stringify(step.output, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Icon per arrival kind, matching the waiting queue and the project overview. */
const ITEM_SOURCE_ICONS: Record<FeedSourceType, typeof FileText> = {
  GRANOLA: Mic,
  SLACK: Hash,
  FILE_UPLOAD: Upload,
  CLI_IMPORT: Terminal,
  MANUAL: FileText,
  MCP: Bot,
  USER_EDIT: Pencil,
  USER_FEEDBACK: MessageSquareText,
  // A mail attachment and a Slack-linked file are both LINKED_DOCUMENT: a paperclip reads as
  // "came with something else", which is exactly what distinguishes it from a deliberate upload.
  LINKED_DOCUMENT: Paperclip,
  EMAIL: Mail,
  DRIVE_FILE: FileText,
  // A person's correction to an automation's document — a pencil, like any other hand edit.
  AUTOMATION_EDIT: Pencil,
};

/**
 * The material this run was handed — one item for an immediate arrival, several for a batch.
 *
 * Worth its own section rather than a count in the stats grid, because it answers a question the run
 * row cannot: a completed run with an empty tally reads as "the agent decided nothing was worth
 * writing", and only the input distinguishes that from "the agent was handed one line of chat".
 *
 * Each item also carries **why it was given to this project** — the reason from the routing decision
 * that chose it, per piece where the source was split. A batch mixes pieces from several arrivals, so
 * this is per item rather than per run: `KbRun.routingId` is only set when every piece shares one
 * decision, and a batch routinely does not.
 */
function RunItems({ items, projectId, runId }: { items: KbRunItem[]; projectId: string; runId: string }) {
  const sourceLabels = useSourceLabels();
  // Empty for a run that predates segment rows, and for one still queued. Saying "0 items" there
  // would assert something false about a run that absorbed plenty.
  if (items.length === 0) return null;

  const chars = items.reduce((sum, item) => sum + item.chars, 0);
  const sources = new Set(items.map((item) => item.source));

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint mb-2 flex items-center gap-1.5">
        <Inbox size={11} />
        Read {items.length} item{items.length === 1 ? '' : 's'}
        <span className="font-normal normal-case tracking-normal text-ink-faint">
          {formatChars(chars)}
          {sources.size > 1 && ` · ${sources.size} sources`}
        </span>
      </p>

      <div className="divide-y divide-border-subtle border border-border rounded-lg overflow-hidden">
        {items.map((item, index) => {
          const Icon = ITEM_SOURCE_ICONS[item.sourceType] ?? FileText;
          return (
            <div key={item.id} className="px-3 py-2.5 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Only for a batch, where the order is load-bearing: the agent read them oldest
                    first, and that is what lets it treat the earlier ones as history. */}
                {items.length > 1 && (
                  <span className="text-[10px] text-ink-faint tabular-nums flex-shrink-0">{index + 1}.</span>
                )}
                <Icon size={11} className="text-ink-faint flex-shrink-0" />
                {/* One line, since it sits in a row of chips — but the full title is on hover, so a
                    long meeting name is not simply unreadable. */}
                <span
                  className="text-xs font-medium text-ink truncate max-w-[16rem]"
                  title={item.title?.trim() || formatSource(item.source, sourceLabels)}
                >
                  {item.title?.trim() || formatSource(item.source, sourceLabels)}
                </span>

                {/* The piece, not the arrival: a source covering two engagements is split before it is
                    routed, so this is what says the run read part of something larger. */}
                {item.total > 1 && (
                  <span
                    title="This source was split into pieces; this project was chosen for this one"
                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-surface-muted text-ink-muted flex-shrink-0"
                  >
                    <Scissors size={9} />
                    piece {item.index} of {item.total}
                  </span>
                )}

                <span className="text-[10px] text-ink-faint flex-shrink-0">{formatChars(item.chars)}</span>

                {/* Its own date, not the run's: a batch spans days, and this is what ordered it and
                    what the agent was told to read it as. */}
                <span
                  className="ml-auto text-[10px] text-ink-faint whitespace-nowrap flex-shrink-0"
                  title={formatDate(item.sourceTimestamp)}
                >
                  {timeAgo(item.sourceTimestamp)}
                </span>
              </div>

              {item.title?.trim() && item.title.trim() !== item.source && (
                <p className="text-[10px] text-ink-faint truncate">from {formatSource(item.source, sourceLabels)}</p>
              )}

              <RunItemContent item={item} projectId={projectId} runId={runId} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * An item's text: the excerpt the run carries, and on request the whole thing.
 *
 * Two levels rather than one, because they answer different questions. The 320-char excerpt says
 * "which piece was this" at a glance for every item in a batch; the full content — fetched only when
 * asked, since a batch can be hundreds of kilobytes behind a tab most openings never visit — is what
 * lets a reviewer check a change against exactly what the agent was shown, without leaving the run.
 * Rendered as the text it was, whitespace kept: this is evidence, not a page.
 */
function RunItemContent({ item, projectId, runId }: { item: KbRunItem; projectId: string; runId: string }) {
  const [full, setFull] = useState<string | null>(null);
  const [showing, setShowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async () => {
    if (showing) {
      setShowing(false);
      return;
    }
    setShowing(true);
    if (full !== null) return;
    setLoading(true);
    setError(null);
    try {
      setFull((await api.kb.runItem(projectId, runId, item.id)).content);
    } catch (e: any) {
      setError(e.message);
      setShowing(false);
    } finally {
      setLoading(false);
    }
  };

  // Nothing beyond the excerpt to fetch: the excerpt is already the whole item.
  const fits = item.chars <= item.excerpt.length && !item.excerpt.endsWith('…');

  return (
    <div className="space-y-1.5">
      {showing && full !== null ? (
        <pre className="text-[11px] text-ink-secondary leading-snug whitespace-pre-wrap break-words font-sans max-h-[28rem] overflow-y-auto rounded-md border border-border-subtle bg-surface-subtle px-3 py-2">
          {full}
        </pre>
      ) : (
        // A clamp, not an expander: the only thing worth opening from here is the whole item, and
        // two "show more" controls stacked — one revealing the rest of a 320-char excerpt — read as a
        // broken one.
        <p className="text-[11px] text-ink-muted leading-snug line-clamp-2">{item.excerpt}</p>
      )}
      {!fits && (
        <button
          onClick={() => void toggle()}
          disabled={loading}
          className="text-[10px] font-medium text-orange-600 dark:text-orange-400 hover:underline disabled:opacity-60"
        >
          {loading ? 'Loading…' : showing ? 'Show excerpt' : `Show full content (${formatChars(item.chars)})`}
        </button>
      )}
      {error && <p className="text-[10px] text-danger-text">{error}</p>}
    </div>
  );
}

/**
 * Where a run wrote, and why.
 *
 * Only for a run that decided this for itself. A pinned run has exactly one destination and it is
 * already in the drawer's header — repeating it as a list of one would imply a choice was made.
 *
 * The reason is the load-bearing part. Once an agent picks the destinations, "why is this page in
 * this project" has no other answer: the run says what it did inside a project, and this says why
 * that project was picked at all.
 */
function RunDestinations({ run, viewerProjectId }: { run: KbRunDetail; viewerProjectId: string }) {
  const destinations = run.destinations ?? [];
  if (!run.triage || destinations.length === 0) return null;

  const placement = run.result?.triage;

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint mb-2 flex items-center gap-1.5">
        <FolderTree size={11} />
        Placed in {destinations.length} project{destinations.length === 1 ? '' : 's'}
      </p>

      {placement?.reasoning && (
        <p className="text-[11px] text-ink-muted leading-snug mb-2">{placement.reasoning}</p>
      )}

      {/* Deciding is charged once for the arrival, whatever it then wrote — so it is stated here,
          above the destinations, rather than divided between them. A run whose bill is mostly this
          line read the whole hierarchy to file one message. */}
      {placement?.usage && placement.usage.costUsd > 0 && (
        <p className="text-[11px] text-ink-faint mb-2" title={describeRunUsage(placement.usage)}>
          Deciding where it belongs cost {formatRunCost(placement.usage.costUsd)}
        </p>
      )}

      <div className="divide-y divide-border-subtle border border-border rounded-lg overflow-hidden">
        {destinations.map((destination) => (
          <div key={destination.projectId} className="px-3 py-2.5 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-ink">{destination.projectName}</span>
              {destination.fellBack && (
                <span
                  title="No project claimed this part, so it was filed at the organization level"
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400"
                >
                  unclaimed
                </span>
              )}
              <span className="text-[10px] text-ink-faint">{formatChars(destination.chars)}</span>
              {destination.confidence > 0 && (
                <span className="text-[10px] text-ink-faint">
                  {Math.round(destination.confidence * 100)}% sure
                </span>
              )}
              {/* What writing this one cost. A run's total says what the arrival cost; this says which
                  project was expensive, which is a fact about the content rather than the run. */}
              {destination.usage && <RunCostChip usage={destination.usage} />}
              {destination.error && (
                <span className="text-[10px] text-danger-text">not written — {destination.error}</span>
              )}
            </div>
            {destination.reason && (
              <p className="text-[11px] text-ink-muted leading-snug">{destination.reason}</p>
            )}

            {/* What this project was actually handed — not the whole arrival, which is what a reader
                would otherwise assume. This is the only record of the cut: the FeedItem holds the
                source and the citations pin sentences, but neither says which parts went where. */}
            {destination.parts.length > 0 && (
              <details>
                <summary className="text-[11px] text-ink-faint cursor-pointer hover:text-ink-secondary">
                  What it was given · {destination.parts.length} part
                  {destination.parts.length === 1 ? '' : 's'}
                </summary>
                <ul className="mt-1.5 space-y-1.5">
                  {destination.parts.map((part) => (
                    <li key={part.itemIndex} className="text-[11px] leading-snug">
                      <span className="text-ink-secondary">{part.title?.trim() || `Item ${part.itemIndex}`}</span>
                      <span className="text-ink-faint">
                        {' '}· item {part.itemIndex} · {formatChars(part.chars)}
                      </span>
                      {/* All of it, not an excerpt: checking a cut means reading exactly what was handed
                          over, and quoting from it. Long parts scroll inside their box. */}
                      <div className="mt-1">
                        <PartText
                          excerpt={part.excerpt}
                          chars={part.chars}
                          truncated={part.truncated}
                          projectId={viewerProjectId}
                          runId={run.id}
                          part={{ destination: destination.projectId, item: part.itemIndex }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ))}
      </div>

      {/* Deliberately discarded material, which is invisible everywhere else: a reader comparing the
          source to the pages would otherwise find text that reached no project and no explanation. */}
      {placement?.discards && placement.discards.length > 0 && (
        <details className="mt-2">
          <summary className="text-[11px] text-ink-faint cursor-pointer hover:text-ink-secondary">
            Threw away {placement.discards.length} part{placement.discards.length === 1 ? '' : 's'} on purpose
          </summary>
          <ul className="mt-1.5 space-y-1.5">
            {placement.discards.map((discard, index) => (
              <li key={index} className="text-[11px] leading-snug">
                <span className="text-ink-secondary">{discard.reason}</span>
                <span className="text-ink-faint"> · {formatChars(discard.chars)}</span>
                <div className="mt-1">
                  <PartText
                    excerpt={discard.excerpt}
                    chars={discard.chars}
                    truncated={discard.chars > discard.excerpt.length}
                    projectId={viewerProjectId}
                    runId={run.id}
                    part={{ discard: index }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Projects it nearly chose. Without these, "nothing else matched" reads identically whether the
          agent named no other project or named one just under the floor — opposite problems. */}
      {placement?.rejected && placement.rejected.length > 0 && (
        <details className="mt-2">
          <summary className="text-[11px] text-ink-faint cursor-pointer hover:text-ink-secondary">
            Considered and not used · {placement.rejected.length}
          </summary>
          <ul className="mt-1.5 space-y-1.5">
            {placement.rejected.map((entry, index) => (
              <li key={index} className="text-[11px] leading-snug">
                <span className="text-ink-secondary">{entry.projectName}</span>
                <span className="text-ink-faint">
                  {' '}· {entry.topic} · {Math.round(entry.confidence * 100)}% — under the floor
                </span>
                <p className="text-ink-faint">{entry.reason}</p>
              </li>
            ))}
          </ul>
        </details>
      )}

      {placement?.notes && placement.notes.length > 0 && (
        <p className="mt-1.5 text-[10px] text-ink-faint leading-snug">{placement.notes.join(' · ')}</p>
      )}
    </div>
  );
}

/**
 * The three questions a run gets asked, one tab each.
 *
 * `report` is the one a person who sent the material reads: what happened to each section of it, in a
 * minute, with what needs them first. The rest are the run as a debugger sees it.
 * `run` is what it did — the live account of it, the tallies, what it committed and the raw trace.
 * `placement` is where it decided the content belonged, which only a run that chose for itself has.
 * `items` is what it was reading while it decided. Stacked on one page these ran to several screens
 * of excerpts, which is what forced every one of them to be clamped to two lines in the first place.
 */
type RunDrawerTab = 'report' | 'run' | 'changes' | 'placement' | 'items';

/**
 * Why a tab is empty, rather than an empty tab.
 *
 * A blank panel under a tab someone deliberately pressed reads as a failure to load — and for both
 * of the tabs that can be empty here the reason is the interesting part, since "this run never chose
 * a destination" and "this run's choice is not recorded" are different facts about the system.
 */
function EmptyPanel({ children }: { children: ReactNode }) {
  return (
    <p className="text-xs text-ink-faint px-4 py-6 border border-dashed border-border rounded-lg text-center leading-relaxed">
      {children}
    </p>
  );
}

function RunDrawer({
  open, run, projectId, projectName, loading, onClose, onOpenNode, onResolved,
}: {
  open: boolean;
  run: KbRunDetail | null;
  projectId: string | null;
  projectName: string | null;
  loading: boolean;
  onClose: () => void;
  onOpenNode: (path: string, changedProjectId: string) => void;
  /** A change reviewed from inside the drawer — the queue and this run both need to hear it. */
  onResolved: (message: string, changedProjectId: string) => void;
}) {
  const [showTrail, setShowTrail] = useState(false);
  const [tab, setTab] = useState<RunDrawerTab>('report');
  const steps = run?.steps?.calls ?? [];
  const sourceLabels = useSourceLabels();

  // A finished run opens on its report; one still moving opens on its timeline, which is the only
  // thing anyone opens a moving run for. Reset whenever a different run is opened.
  useEffect(() => {
    setTab(run && isRunLive(run.status) ? 'run' : 'report');
    setShowTrail(false);
  }, [run?.id]);

  /**
   * The live account of this run.
   *
   * Keyed on the run's id rather than on the drawer being open, so the stream is torn down the moment
   * a different run is opened — an EventSource left attached to the previous one would keep a
   * connection (and a Redis connection behind it) for a run nobody is looking at.
   */
  const { timeline, live, loading: timelineLoading, error: streamError } = useRunStream(
    projectId,
    run?.id ?? null,
    run?.status ?? null,
  );
  const headline = useLiveHeadline(timeline);

  // Empty for a pinned run, which had nothing to decide — the tab says so rather than vanishing, so
  // that "where is the placement?" has an answer on the page instead of in the tab strip's shape.
  const destinations = run?.destinations ?? [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={formatRunLabel(run?.fileName, sourceLabels) || run?.source || 'Run'}
      className="max-w-3xl"
    >
      {loading && !run ? (
        <p className="text-sm text-ink-faint">Loading…</p>
      ) : !run ? (
        <p className="text-sm text-ink-faint">Run not found.</p>
      ) : (
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Badge variant={run.status === 'FAILED' ? 'danger' : run.status === 'COMPLETED' ? 'success' : 'warning'}>
              {run.status}
            </Badge>
            {/* The same two glyphs the list uses, and for the same reason: for a routed run this tag
                is the scope the content was read *from*, not where it went — which the destinations
                panel below states in full, so the header only has to say which kind of run this is. */}
            <span
              className="flex items-center gap-1"
              title={
                run.triage
                  ? 'Auto-routed — this arrived with no destination, so the ingestor chose one from this scope'
                  : 'Pinned — this arrived with its destination already set, so no routing was done'
              }
            >
              <PlacementMark triage={run.triage} />
              {run.triage && <span className="text-[10px] text-ink-faint">read from</span>}
              <ProjectTag name={projectName} />
            </span>
            <span className="text-[11px] text-ink-faint font-mono">{run.sourceType}</span>
            <span className="ml-auto text-xs text-ink-faint">{formatDate(run.queuedAt)}</span>
          </div>

          {/* Three questions about one run, and stacking them made a page nobody could hold: what it
              did, where it decided the content belonged, and what it was reading when it decided.
              They are asked one at a time — and each is now free to be as long as it needs to be,
              which is what lets the excerpts open in place rather than being clamped to keep the
              scroll bearable.

              Pinned, so switching tabs never means scrolling back up a screen of excerpts to reach
              the strip. Both offsets here are compensating for the box this sits in, and neither is
              cosmetic — a pinned bar that does not reach the edges of its scroller is not pinned to
              anything, it is a bar with the list running through the gap in the open:

              `-top-4` cancels the drawer body's own `py-4`. A sticky element pins to the scrollport
              *inside* the scroll container's padding, so `top-0` pins it 16px down and leaves 16px of
              live content above it — which is one whole row of a list like this one. The negative
              offset moves the pin up by exactly that padding, and the `pt-4` inside the bar puts the
              same 16px back where it belongs, above the labels instead of above the bar.

              `-mx-6 px-6` is the same trick horizontally, against that box's `px-6`, so the strip's
              background and its underline run edge to edge.

              It also carries **no margin of its own**: sticky pins the *margin* box, so a `space-y`
              parent's 20px gap would reappear as a second band. The space above it belongs to the
              header, the space below to each panel. */}
          <div className="sticky -top-4 z-10 -mx-6 flex items-center gap-5 border-b border-border bg-card px-6 pt-4">
            <TabButton active={tab === 'report'} onClick={() => setTab('report')} label="Report" count={null} />
            <TabButton active={tab === 'run'} onClick={() => setTab('run')} label="Run" count={null} />
            <TabButton
              active={tab === 'changes'}
              onClick={() => setTab('changes')}
              label="Changes"
              count={run.changes.length}
              tone={run.changes.some((c) => c.reviewStatus === 'PENDING') ? 'live' : 'neutral'}
            />
            <TabButton
              active={tab === 'placement'}
              onClick={() => setTab('placement')}
              label="Placement"
              count={destinations.length}
              tone="neutral"
            />
            <TabButton
              active={tab === 'items'}
              onClick={() => setTab('items')}
              label="Items read"
              count={run.items.length}
              tone="neutral"
            />
          </div>

          {tab === 'report' && (
            <div className="mt-5">
              {projectId ? (
                <RunReportPanel run={run} projectId={projectId} onOpenNode={onOpenNode} onOpenChanges={() => setTab('changes')} />
              ) : (
                <EmptyPanel>Open this run from a project to see its report.</EmptyPanel>
              )}
            </div>
          )}

          {tab === 'changes' && (
            <div className="mt-5">
              {/* The same cards the review queue renders — diff from the change's own snapshots, the
                  agent's reasoning, the citations, and the accept / revert / keep-both actions — so a
                  run's pages can be judged here without hunting for them in the queue. One run's
                  changes rather than a filter on the queue, because the question here is "what did
                  *this* run do", and the queue answers "what is waiting". */}
              {run.changes.length === 0 ? (
                <p className="text-xs text-ink-faint px-4 py-5 border border-dashed border-border rounded-lg text-center">
                  {isRunLive(run.status)
                    ? 'Nothing committed yet.'
                    : 'The agent committed an empty plan — the content held nothing durable.'}
                </p>
              ) : (
                <div className="space-y-2.5">
                  {run.changes.map((change) => (
                    <ChangeCard
                      key={change.id}
                      change={change}
                      // The request scope, as everywhere: a parent may act on a descendant's change.
                      projectId={projectId ?? run.projectId}
                      onResolved={onResolved}
                      onOpenNode={onOpenNode}
                      // No rail in here: the drawer is max-w-3xl, and a 248px column would take a
                      // third of it off the diff. Evidence falls back to the panel under the card.
                      evidenceRail={false}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'run' && (
            <div className="mt-5 space-y-5">
              {/* First, not last: for a run in flight this is the only thing anyone opened the drawer
                  for, and the tallies below it are all still zero. */}
              <div className="space-y-3">
                <RunPhaseHeader timeline={timeline} headline={headline} live={live} />
                <RunTimelineView timeline={timeline} live={live} loading={timelineLoading} />
                {streamError && (
                  <p className="text-[11px] text-ink-faint">
                    {streamError} The record below is still complete up to where it stopped.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-6 gap-3 text-xs">
                <Stat label="Steps" value={String(run.toolCalls)} />
                <Stat label="Chunks" value={String(run.chunks)} />
                <RunCostStats usage={runUsageOf(run)} />
                <Stat label="Started" value={run.startedAt ? timeAgo(run.startedAt) : '—'} />
                <Stat label="Finished" value={run.finishedAt ? timeAgo(run.finishedAt) : '—'} />
              </div>

              <div className="grid grid-cols-5 gap-3 text-xs">
                <Stat label="Created" value={String(run.nodesCreated)} />
                <Stat label="Amended" value={String(run.nodesUpdated)} />
                <Stat label="Removed" value={String(run.nodesDeleted)} />
                <Stat label="Merged" value={String(run.nodesMerged)} />
                <Stat label="Moved" value={String(run.nodesMoved)} />
              </div>

              {run.errorMessage && (
                <div className="px-4 py-3 rounded-md bg-danger-bg border border-danger-ring text-sm text-danger-text">
                  <AlertTriangle size={13} className="inline mr-1.5 -mt-0.5" />
                  {run.errorMessage}
                </div>
              )}

              {run.result?.skipped && run.result.skipped.length > 0 && (
                <Section title="Skipped">
                  <ul className="space-y-1">
                    {run.result.skipped.map((s, i) => (
                      <li key={i} className="text-[11px] text-ink-muted font-mono">{s}</li>
                    ))}
                  </ul>
                </Section>
              )}

              {run.result?.failed && run.result.failed.length > 0 && (
                <Section title="Failed">
                  <ul className="space-y-1">
                    {run.result.failed.map((f, i) => (
                      <li key={i} className="text-[11px] text-danger-text font-mono">{f}</li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* What the guardrail kept out. Categories and reasons only — the run row never holds the
                  text, which is in the admin guardrail log. Otherwise the gap between the source and the
                  pages written from it would read as the agent losing material. */}
              {run.result?.withheld && run.result.withheld.length > 0 && (
                <Section title={`Withheld by the guardrail (${run.result.withheld.length})`}>
                  <ul className="space-y-1">
                    {run.result.withheld.map((w, i) => (
                      <li key={i} className="text-[11px] leading-snug">
                        <span className="text-ink-secondary">{guardrailCategoryLabel(w.category)}</span>
                        <span className="text-ink-faint">
                          {' '}· {w.reason} · {formatChars(w.chars)} · {Math.round(w.confidence * 100)}% ·{' '}
                          {w.phase === 'PLACEMENT' ? 'cut before writing' : 'refused while writing'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* What landed in the tree. The queue tab judges these one at a time; here they are
                  grouped by the run that produced them. */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
                    Changes ({run.changes.length})
                  </p>
                  {run.changes.length > 0 && (
                    <button
                      onClick={() => setTab('changes')}
                      className="text-[11px] text-orange-600 dark:text-orange-400 hover:underline"
                    >
                      See the diffs →
                    </button>
                  )}
                </div>
                {run.changes.length === 0 ? (
                  <p className="text-xs text-ink-faint px-4 py-5 border border-dashed border-border rounded-lg text-center">
                    {isRunLive(run.status)
                      ? 'Nothing committed yet.'
                      : 'The agent committed an empty plan — the content held nothing durable.'}
                  </p>
                ) : (
                  <div className="divide-y divide-border-subtle border border-border rounded-lg overflow-hidden">
                    {run.changes.map((change) => {
                      const meta = KIND_META[change.kind];
                      const Icon = meta.icon;
                      return (
                        <button
                          key={change.id}
                          onClick={() => onOpenNode(change.path, change.projectId)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-subtle transition-colors"
                        >
                          <Icon size={12} className={cn('flex-shrink-0', meta.tone)} />
                          <span className="text-xs text-ink truncate">{change.afterTitle ?? change.beforeTitle ?? change.path}</span>
                          <NodePath change={change} variant="inline" />
                          <span className="ml-auto flex items-center gap-2 flex-shrink-0">
                            <ConfidenceChip value={change.confidence} />
                            <StatusBadge status={change.reviewStatus} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* The only place a person can tell the system it got something wrong. Under the changes rather than above
                  them: a verdict is passed after the evidence, and the pages it wrote are the last of
                  it. Still on this tab rather than one of its own, because what is judged is the run
                  as a whole — a report's kind already says which half of the work it is about. */}
              <Section title="Was this right?">
                <RunFeedback run={run} />
              </Section>

              {/* The raw trace, collapsed. The timeline above is the readable account of the same run;
                  this is the unfiltered tool-by-tool record, kept because it is the only view of the
                  exact inputs and outputs — and the only one that exists for runs that predate
                  streaming. */}
              {steps.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowTrail((s) => !s)}
                    className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint hover:text-ink-muted transition-colors mb-2"
                  >
                    {showTrail ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    Raw trace ({steps.length} steps)
                  </button>
                  {showTrail && <StepTrail steps={steps} />}
                </div>
              )}
            </div>
          )}

          {/* Where it went, and why. Its own tab because for a routed run this is the first question
              asked of it, and the answer — the reasoning, what each project was handed, what was
              thrown away, what was nearly chosen — is longer than anything else here. */}
          {tab === 'placement' && (
            <div className="mt-5">
              {run.triage && destinations.length > 0 ? (
                <RunDestinations run={run} viewerProjectId={projectId ?? run.projectId} />
              ) : (
                <EmptyPanel>
                  {run.triage
                    ? 'This run has not chosen its destinations yet — placement is its first phase.'
                    : `This run arrived with its destination already set${projectName ? ` — ${projectName}` : ''}, so it had nothing to place.`}
                </EmptyPanel>
              )}
            </div>
          )}

          {/* What went in, as against what came out. A run's counters say how many pages moved; this
              says how much material moved them, which for a batch is the difference between "the agent
              did very little" and "the agent was given very little". */}
          {tab === 'items' && (
            <div className="mt-5">
              {run.items.length > 0 ? (
                <RunItems items={run.items} projectId={projectId ?? run.projectId} runId={run.id} />
              ) : (
                <EmptyPanel>
                  {isRunLive(run.status)
                    ? 'Nothing claimed yet — a run takes its items when it starts.'
                    : 'No items are recorded for this run. Runs from before arrivals were tracked as segments have none.'}
                </EmptyPanel>
              )}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-ink-faint">{label}</p>
      <p className="text-ink tabular-nums">{value}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint mb-2">{title}</p>
      {children}
    </div>
  );
}

/**
 * Room for the evidence rail, for everything on the queue that is *not* a change card.
 *
 * The container is wider than the cards by the rail and its gap, so a full-width row — the title,
 * the filters, the project chips, an empty state — would otherwise run out across a gutter that
 * only exists to hold evidence, and stop lining up with the cards beneath it. Padding rather than
 * a max-width so it composes with whatever each row already sets, and one constant because the
 * number has to agree with the rail's own width (248px) plus the card row's `gap-4`.
 *
 * A literal, not a template: Tailwind scans source text, so a computed class name generates no CSS.
 */
const RAIL_ROOM = 'md:pr-[264px]';

const FILTERS: Array<{ label: string; value: KbReviewStatus | 'ALL' }> = [
  { label: 'Awaiting review', value: 'PENDING' },
  { label: 'Auto-applied', value: 'NOT_REQUIRED' },
  { label: 'Accepted', value: 'ACCEPTED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'Kept both', value: 'KEPT_BOTH' },
  { label: 'Superseded', value: 'SUPERSEDED' },
  { label: 'Everything', value: 'ALL' },
];

type Tab = 'queue' | 'runs';

/**
 * Which queue layout this reviewer is looking at. The card layout is the default; the classic one is
 * kept exactly as it was, one click away, while people settle into the new one. Browser storage
 * because it is a per-person convenience, and guarded because storage can be unavailable.
 */
type QueueLayout = 'cards' | 'classic';
const LAYOUT_KEY = 'kbReview.layout';

function readLayout(): QueueLayout {
  try {
    return window.localStorage.getItem(LAYOUT_KEY) === 'classic' ? 'classic' : 'cards';
  } catch {
    return 'cards';
  }
}

function storeLayout(layout: QueueLayout) {
  try {
    window.localStorage.setItem(LAYOUT_KEY, layout);
  } catch {
    // Remembering the choice is a nicety; the switch still works for this visit.
  }
}

/**
 * Two pages, one component. The review queue and the run list were tabs of one page until the runs
 * moved to their own sidebar section (Memory System Activity) — but they still share the scope bar,
 * the level chips, the drawer and the loading, so the split is a prop and two routes rather than a
 * second thousand-line file. `view` replaces what used to be `?tab=`.
 */
export default function KbReview({ view = 'queue' }: { view?: Tab } = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * Reviewing is supervisory, so the default is the whole organization: every project's applied
   * changes in one queue, narrowed by the scope bar and the level chips rather than by which URL you
   * happened to open. Before this the page could only ever show one project's branch, which meant a
   * reviewer had to remember where work might be waiting.
   */
  const scope = useScope();
  const tab: Tab = view;

  /**
   * Activity is pinned to the whole organization and carries no scope bar: the page is a monitor —
   * every ingestion, newest first, each row naming its project — and narrowing was its only
   * control. It deliberately ignores the shared scope selection too, because a project picked on
   * the Feed or the review queue would otherwise filter this list with nothing on screen to say so.
   */
  const { organization } = useProjectTree();
  const projectId = tab === 'runs' ? organization?.id ?? null : scope.resolvedProjectId;
  const scopeMode = tab === 'runs' ? ('cone' as const) : scope.mode;

  /**
   * The open run lives in the URL rather than in state: a run is a thing people link each other
   * to ("look at what ingestion did to this page"). The tab used to live there too — `?tab=runs`
   * on this route now belongs to /activity, and the effect below forwards old links there.
   */
  const openRunId = searchParams.get('run');

  useEffect(() => {
    if (view === 'queue' && searchParams.get('tab') === 'runs') {
      const run = searchParams.get('run');
      navigate(`/activity${run ? `?run=${run}` : ''}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, searchParams]);

  const [status, setStatus] = useState<KbReviewStatus | 'ALL'>('PENDING');
  /** Empty means every level in scope; otherwise the levels whose chips are selected. */
  const [levelFilter, setLevelFilter] = useState<string[]>([]);
  /**
   * The queue's default lens: the projects where the reviewer holds an ADMIN or MEMBER seat, not
   * everything cone visibility lets them see. Anyone can still adjudicate anything — flipping to
   * "All projects" is one click — but the items someone is *expected* to work are the ones in
   * projects they own, so that is what the page opens on. Only meaningful in cone mode: picking a
   * single project with "this project alone" is already an explicit choice, and second-guessing it
   * with a membership filter would empty the page for no visible reason.
   */
  const [mineOnly, setMineOnly] = useState(true);
  /** Set once the reviewer touches the lens — the auto-widen below must never fight a choice. */
  const mineTouched = useRef(false);
  const [changes, setChanges] = useState<KbChange[]>([]);
  const [levels, setLevels] = useState<ProjectScopeLevel[]>([]);
  const [layout, setLayoutState] = useState<QueueLayout>(readLayout);
  const setLayout = (next: QueueLayout) => {
    storeLayout(next);
    setLayoutState(next);
  };
  const [runs, setRuns] = useState<KbRun[]>([]);
  /**
   * How many run rows have been fetched under the current scope — which is the next request's
   * offset. Counted rather than read off `runs.length`, because `mergeRuns` can absorb a page
   * without the list growing; an offset derived from the length would then ask for that same page
   * forever, and the sentinel below would keep asking.
   */
  const [runsFetched, setRunsFetched] = useState(0);
  /** A full page came back, so there is probably another. The only evidence the endpoint gives. */
  const [moreRuns, setMoreRuns] = useState(false);
  const [loadingMoreRuns, setLoadingMoreRuns] = useState(false);
  /** A failed *page* fetch, kept apart from `error` — the list above it is still good. */
  const [runsError, setRunsError] = useState<string | null>(null);
  /** The page's scroll container and the marker at the end of the run list — see the observer below. */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const runsSentinelRef = useRef<HTMLDivElement | null>(null);
  /**
   * A page is in flight. A ref as well as `loadingMoreRuns`, because the observer can fire twice
   * before a `setState` has been applied — two remounts of the effect, or two entries in the same
   * tick — and the state flag is stale in the second call's closure. Two fetches at one offset would
   * not double a row (`mergeRuns` sees to that) but would advance `runsFetched` twice, skipping a
   * whole page of runs that nothing would ever ask for again.
   */
  const runsInFlight = useRef(false);
  const [openRun, setOpenRun] = useState<KbRunDetail | null>(null);
  const [runLoading, setRunLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setOpenRunId = (runId: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (runId) params.set('run', runId);
    else params.delete('run');
    setSearchParams(params, { replace: true });
  };

  /**
   * Loads what this page actually renders, and nothing else. The queue and the run list used to be
   * fetched together — a habit from when they were tabs of one page and switching had to be free.
   * As two pages, that meant each 4-second poll pulled the *other* page's data to render none of
   * it: the queue's hundred change rows on Activity, the runs' destination excerpts on Review.
   */
  const load = useCallback((quiet = false) => {
    if (!projectId) return;
    // A poll must not flash the list back to "Loading…" every few seconds — only a deliberate
    // load (first paint, filter change, a decision that changed the queue) shows that.
    if (!quiet) setLoading(true);
    const mine = mineOnly && scope.mode === 'cone';

    const page =
      tab === 'queue'
        ? api.kb
            .changes(projectId, {
              reviewStatus: status === 'ALL' ? undefined : status,
              scope: scope.mode,
              projectIds: levelFilter.length > 0 ? levelFilter : undefined,
              mine,
              limit: 100,
              reviewedSince: localMidnight(),
            })
            .then((c) => {
              setChanges(c.changes);
              setLevels(c.levels);
              // A reviewer with no seat anywhere in this scope would otherwise open onto a queue
              // that is empty forever with no visible cause — widen for them, once, and never
              // against a choice.
              if (!quiet && mine && !mineTouched.current && c.levels.length > 0 && !c.levels.some((l) => l.mine)) {
                setMineOnly(false);
              }
            })
        : // Runs follow the same scope: read from the organization it shows every project's runs,
          // which is what makes "an ingestion just happened somewhere" visible at all.
          //
          // Always the *first* page, even when several have been scrolled in: a run row carries its
          // destinations and their excerpts, so re-reading a deep window every four seconds would be
          // hundreds of kilobytes a poll to refresh rows that finished hours ago. A run that is
          // still moving was queued recently, which in a `queuedAt desc` list means the top of it —
          // the worst burst measured here was 29 runs in sixteen seconds, well inside one page.
          api.kb.runs(projectId, { scope: scopeMode, limit: RUNS_PAGE }).then((r) => {
            // A poll re-reads the first page and folds it in, leaving whatever has been paged in
            // below it alone. A deliberate load is a different list — new scope, new filters — so
            // it replaces, and the paging window starts again with it.
            if (quiet) {
              setRuns((prev) => mergeRuns(prev, r));
            } else {
              setRuns(r);
              setRunsFetched(r.length);
              setMoreRuns(r.length === RUNS_PAGE);
              setRunsError(null);
            }
          });

    page
      .then(() => setError(null))
      .catch((e) => setError(e.message))
      .finally(() => { if (!quiet) setLoading(false); });
  }, [projectId, status, scope.mode, levelFilter, mineOnly, tab]);

  useEffect(() => { load(); }, [load]);

  // A level chip only means something within the scope that produced it; carrying a selection across
  // a scope change would filter the queue down to projects that are no longer in it — an empty list
  // with no visible cause. The lens resets with it: a chip picked under one lens may not exist
  // under the other.
  /** Set by `pickProject` when it widens the lens to reach the project it picked — see there. */
  const keepLevelFilter = useRef(false);
  useEffect(() => {
    if (keepLevelFilter.current) {
      keepLevelFilter.current = false;
      return;
    }
    setLevelFilter([]);
  }, [scope.projectId, scope.mode, mineOnly]);

  /**
   * Narrows the queue to one project (a tile, or a row under All projects), or clears with null.
   * A project outside the My projects lens is dropped by the server's filter, which would read as
   * the click doing nothing — so picking one widens the lens, and the reset above is told to keep
   * the pick rather than clear it for the lens change it caused.
   */
  const pickProject = (id: string | null) => {
    if (!id) {
      setLevelFilter([]);
      return;
    }
    const level = levels.find((l) => l.id === id);
    if (mineOnly && scope.mode === 'cone' && level && !level.mine) {
      mineTouched.current = true;
      keepLevelFilter.current = true;
      setMineOnly(false);
    }
    setLevelFilter([id]);
  };

  /** Fetches the open run's detail — the changes it made and its step trail. */
  const loadRun = useCallback(async () => {
    if (!projectId || !openRunId) return;
    try {
      setOpenRun(await api.kb.run(projectId, openRunId));
    } catch (e: any) {
      setError(e.message);
    }
  }, [projectId, openRunId]);

  useEffect(() => {
    if (!openRunId) { setOpenRun(null); return; }
    // Cleared first, so opening a second run never shows the first one's trail while it loads.
    setOpenRun(null);
    setRunLoading(true);
    loadRun().finally(() => setRunLoading(false));
  }, [openRunId, loadRun]);

  /**
   * Any run still moving — the reason to keep polling. A KB run is a minute of model time. On the
   * queue page the run list is never fetched, so this reduces to "a live run is open in the
   * drawer": the queue refreshes on every decision and filter change, and stays still otherwise.
   */
  const hasLiveRun = useMemo(
    () => runs.some((r) => isRunLive(r.status)) || (openRun !== null && isRunLive(openRun.status)),
    [runs, openRun],
  );

  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    if (!hasLiveRun) return;
    pollRef.current = window.setInterval(() => {
      load(true);
      loadRun();
    }, POLL_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [hasLiveRun, load, loadRun]);

  /**
   * The next page of runs, folded onto the end of the list.
   *
   * Offset paging because that is what the endpoint takes; the shift it is prone to — a run arriving
   * at the top mid-scroll, moving everything down a row — costs a repeated row rather than a lost
   * one, and `mergeRuns` absorbs exactly that.
   */
  const fetchMoreRuns = useCallback(() => {
    if (!projectId || !moreRuns || loadingMoreRuns || runsInFlight.current) return;
    runsInFlight.current = true;
    setLoadingMoreRuns(true);
    setRunsError(null);
    api.kb
      .runs(projectId, { scope: scopeMode, limit: RUNS_PAGE, offset: runsFetched })
      .then((page) => {
        setRuns((prev) => mergeRuns(prev, page));
        setRunsFetched((n) => n + page.length);
        setMoreRuns(page.length === RUNS_PAGE);
      })
      .catch((e) => setRunsError(e.message))
      .finally(() => {
        runsInFlight.current = false;
        setLoadingMoreRuns(false);
      });
  }, [projectId, scopeMode, runsFetched, moreRuns, loadingMoreRuns]);

  /**
   * The end of the list, watched instead of clicked.
   *
   * Reading the runs tab is scrolling back through a day of ingestion, and a button every fifty rows
   * interrupts that with a decision that has only one answer. `root` is the page's own scroll
   * container rather than the viewport, which is what makes the margin work at all: the list scrolls
   * inside that container, so a viewport-relative margin is clipped away by it and the next page
   * would only start loading once the reader had already hit the bottom.
   */
  useEffect(() => {
    const el = runsSentinelRef.current;
    // Nothing to watch until the tab has a list, and nothing to ask for once it is exhausted or the
    // last page failed — auto-paging past an error retries it as fast as the network allows.
    if (!el || !moreRuns || runsError) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) fetchMoreRuns(); },
      { root: scrollRef.current, rootMargin: '400px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchMoreRuns, moreRuns, runsError, tab]);

  // Nothing else to settle: the per-node pending badges live in the knowledge base's own pages
  // panel, whose tree is fetched afresh whenever that view is opened.
  const resolved = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 6000);
    load();
  };

  /** A change's own project decides the link — the queue spans several, and paths repeat across them. */
  const openNode = (path: string, changedProjectId: string) => {
    const project = scope.projectFor(changedProjectId);
    if (project) navigate(`/p/${project.slug}/kb/${path}`);
  };

  const scopeLabel =
    tab === 'runs' || scope.isEverything
      ? 'every project'
      : `${scope.project?.name ?? 'this project'}${scope.mode === 'cone' ? ' and its sub-projects' : ''}`;

  /**
   * What is actually waiting, taken from the levels' counts rather than the loaded list — the list
   * is whatever filter is on, so "Everything" would otherwise badge itself. A `self` scope returns
   * no levels, and there the loaded list is the only source; null when even that can't be honest,
   * which is better than a confidently wrong number on a supervisory page.
   */
  const pendingCount =
    levels.length > 0
      ? levels.reduce((sum, l) => sum + l.pendingChanges, 0)
      : status === 'PENDING' || status === 'ALL'
        ? changes.filter((c) => c.reviewStatus === 'PENDING').length
        : null;
  const liveRunCount = runs.filter((r) => isRunLive(r.status)).length;
  /** The tab strip these used to badge is gone; the header keeps the signal per page. */
  const headerCount = tab === 'queue' ? pendingCount : liveRunCount || null;

  return (
    <div className="flex h-screen overflow-hidden bg-surface">

      <div ref={scrollRef} className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] flex-1 h-full overflow-y-auto">
        {/* The queue is wider than the rest by exactly the evidence rail plus its gap (248 + 16),
            so the cards keep the 4xl they always had and the rail occupies new space rather than
            eating into the diff. A centred container growing on the right moves its left edge left,
            which is what shifts the whole queue over. Activity keeps the original width: it has no
            rail, and widening it would leave its cards stretched for nothing. */}
        {tab === 'queue' && layout === 'cards' && projectId ? (
          <ReviewQueue
            changes={changes}
            levels={levels}
            pendingCount={pendingCount}
            status={status}
            onStatus={setStatus}
            levelFilter={levelFilter}
            onPickProject={pickProject}
            mineOnly={mineOnly}
            onMineOnly={(mine) => {
              mineTouched.current = true;
              setMineOnly(mine);
            }}
            scope={scope}
            projectId={projectId}
            loading={loading}
            error={error}
            toast={toast}
            onResolved={resolved}
            onOpenNode={openNode}
            onClassic={() => setLayout('classic')}
          />
        ) : (
        <div className={cn('mx-auto px-7 py-7', tab === 'queue' ? 'max-w-[73rem]' : 'max-w-4xl')}>
          {/* The scope controls share the header line: the picker is the answer to "review of
              what", so it reads as part of the title rather than as a filter row below it. The
              queue's explanatory sentence is gone on purpose — the empty state and the guide carry
              it, and a supervisory page opens onto the work, not onto prose. Activity keeps its
              one-liner and no scope bar: it is pinned to the whole organization above. */}
          <div className={cn('mb-4 flex items-start justify-between gap-x-4 gap-y-2 flex-wrap', tab === 'queue' && RAIL_ROOM)}>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-ink flex items-center gap-2">
                {tab === 'queue' ? 'Knowledge base review' : 'Memory System Activity'}
                {tab === 'queue' && (
                  <button
                    onClick={() => setLayout('cards')}
                    className="ml-1 text-[11px] font-medium text-orange-600 hover:text-orange-700"
                  >
                    New layout
                  </button>
                )}
                {headerCount !== null && headerCount > 0 && (
                  <span className="text-[11px] font-semibold text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-950/50 rounded-full px-2 py-0.5">
                    {headerCount}{tab === 'runs' ? ' live' : ''}
                  </span>
                )}
              </h1>
            </div>
            {tab === 'queue' && <ScopeBar scope={scope} />}
          </div>

          {tab === 'queue' && (
            <div className={cn('flex items-center gap-1 mb-4 flex-wrap', RAIL_ROOM)}>
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setStatus(f.value)}
                  className={cn(
                    'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border',
                    status === f.value
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-card text-ink-muted border-border hover:border-orange-300 hover:text-ink',
                  )}
                >
                  {f.label}
                </button>
              ))}

              {/* The lens: whose projects the queue shows. Defaults to the reviewer's own — the
                  changes someone is expected to work are the ones in projects they hold a seat in —
                  and only renders when the two lenses actually differ. Cone mode only: a single
                  project picked with "this project alone" is already an explicit choice. */}
              {scope.mode === 'cone' && levels.some((l) => l.mine) && levels.some((l) => !l.mine) && (
                <div className="ml-auto flex items-center gap-1">
                  {([
                    { label: 'My projects', value: true },
                    { label: 'All projects', value: false },
                  ] as const).map((lens) => (
                    <button
                      key={lens.label}
                      onClick={() => {
                        mineTouched.current = true;
                        setMineOnly(lens.value);
                      }}
                      className={cn(
                        'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border',
                        mineOnly === lens.value
                          ? 'bg-ink text-surface border-ink'
                          : 'bg-card text-ink-muted border-border hover:border-orange-300 hover:text-ink',
                      )}
                    >
                      {lens.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* One chip per level in scope, with its share of the queue. Selecting none means all —
              so the filter never has to be reset to see everything, and a level that goes quiet
              doesn't silently disappear from view. Under the "My projects" lens only the reviewer's
              own levels are offered, since the others cannot narrow a queue they are not in. */}
          {tab === 'queue' && scope.mode === 'cone' && levels.filter((l) => !mineOnly || l.mine).length > 1 && (
            <div className={cn('flex items-center gap-1 mb-4 flex-wrap', RAIL_ROOM)}>
              {levels.filter((l) => !mineOnly || l.mine).map((level) => {
                const selected = levelFilter.includes(level.id);
                return (
                  <button
                    key={level.id}
                    onClick={() =>
                      setLevelFilter((prev) =>
                        prev.includes(level.id) ? prev.filter((id) => id !== level.id) : [...prev, level.id],
                      )
                    }
                    className={cn(
                      'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border inline-flex items-center gap-1.5',
                      selected
                        ? 'bg-ink text-surface border-ink'
                        : 'bg-card text-ink-muted border-border hover:border-orange-300 hover:text-ink',
                    )}
                  >
                    {level.name}
                    {level.pendingChanges > 0 && (
                      <span
                        className={cn(
                          'text-[9px] font-semibold px-1 rounded-full',
                          selected ? 'bg-white/25' : 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400',
                        )}
                      >
                        {level.pendingChanges}
                      </span>
                    )}
                  </button>
                );
              })}
              {levelFilter.length > 0 && (
                <button
                  onClick={() => setLevelFilter([])}
                  className="px-2 py-1 text-[11px] text-ink-faint hover:text-ink transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {toast && (
            <p className={cn('mb-4 text-xs text-ink-secondary bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded px-3 py-2', tab === 'queue' && RAIL_ROOM)}>
              {toast}
            </p>
          )}
          {error && (
            <p className={cn('mb-4 text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2', tab === 'queue' && RAIL_ROOM)}>{error}</p>
          )}

          {loading ? (
            <p className={cn('text-xs text-ink-faint py-8 text-center', tab === 'queue' && RAIL_ROOM)}>Loading…</p>
          ) : tab === 'queue' ? (
            changes.length === 0 ? (
              // Card rather than a bare dashed outline, matching the Automations empty state — the
              // sibling pages should read as one product when both are quiet.
              <Card className={cn('px-6 py-14 text-center', RAIL_ROOM)}>
                <Check size={22} className="text-ink-faint mx-auto mb-3" />
                <p className="text-sm text-ink-secondary mb-1">
                  {status === 'PENDING' ? 'Nothing awaiting review' : 'No changes here'}
                </p>
                <p className="text-xs text-ink-faint max-w-sm mx-auto">
                  {status === 'PENDING'
                    ? 'Every change the agent has made so far cleared its confidence floor.'
                    : 'Try another filter.'}
                </p>
                {/* The lens can hide real work: say so rather than letting an admin conclude the
                    org's queue is clear when it is merely not theirs. */}
                {mineOnly && scope.mode === 'cone' && levels.some((l) => !l.mine && l.pendingChanges > 0) && (
                  <p className="text-xs text-ink-faint mt-2">
                    {levels.filter((l) => !l.mine).reduce((sum, l) => sum + l.pendingChanges, 0)} pending in
                    projects outside yours —{' '}
                    <button
                      onClick={() => {
                        mineTouched.current = true;
                        setMineOnly(false);
                      }}
                      className="text-orange-500 hover:text-orange-600 underline"
                    >
                      show all projects
                    </button>
                  </p>
                )}
              </Card>
            ) : (
              <div className="space-y-2.5">
                {changes.map((change) => (
                  <ChangeCard
                    key={change.id}
                    change={change}
                    projectId={projectId!}
                    onResolved={resolved}
                    onOpenNode={openNode}
                  />
                ))}
              </div>
            )
          ) : runs.length === 0 ? (
            <Card className="px-6 py-14 text-center">
              <Zap size={22} className="text-ink-faint mx-auto mb-3" />
              <p className="text-sm text-ink-secondary mb-1">No ingestion runs yet</p>
              <p className="text-xs text-ink-faint max-w-sm mx-auto">
                Runs appear here as content arrives — from Slack, Granola, a file upload, or the router.
              </p>
            </Card>
          ) : (
            <>
              <div className="divide-y divide-border-subtle border border-border rounded-lg overflow-hidden bg-card">
                {runs.map((run) => (
                  <RunRow
                    key={run.id}
                    run={run}
                    projectName={scope.projectFor(run.projectId)?.name ?? null}
                    onOpen={() => setOpenRunId(run.id)}
                  />
                ))}
              </div>

              {/* Outside the card rather than inside it: the list is a `divide-y` stack, and an
                  empty marker in there draws a hairline under the last run. */}
              <div ref={runsSentinelRef} aria-hidden className="h-px" />

              <div className="py-3 min-h-[2.25rem] flex items-center justify-center gap-2 text-[11px]">
                {loadingMoreRuns ? (
                  <>
                    <BreathingDot size={6} tone="ink" />
                    <span className="text-ink-faint">Loading earlier runs…</span>
                  </>
                ) : runsError ? (
                  <>
                    <span className="text-danger-text">{runsError}</span>
                    <button
                      onClick={() => { setRunsError(null); fetchMoreRuns(); }}
                      className="text-ink-muted hover:text-ink underline underline-offset-2"
                    >
                      Try again
                    </button>
                  </>
                ) : (
                  // Said only once the list has actually been paged: on a first screenful it would
                  // be labelling the obvious, and the reader has no reason yet to wonder whether
                  // scrolling further would find more.
                  !moreRuns && runs.length > RUNS_PAGE && (
                    <span className="text-ink-faint">
                      {runs.length} runs — that's every one in {scopeLabel}
                    </span>
                  )
                )}
              </div>
            </>
          )}
        </div>
        )}
      </div>

      <RunDrawer
        open={openRunId !== null}
        run={openRun}
        projectId={projectId}
        projectName={scope.projectFor(openRun?.projectId)?.name ?? null}
        loading={runLoading}
        onClose={() => setOpenRunId(null)}
        onOpenNode={openNode}
        onResolved={(message) => {
          resolved(message);
          void loadRun();
        }}
      />
    </div>
  );
}

/**
 * Tones for a tab's count, which is the whole reason this takes one.
 *
 * A number beside a label reads as a demand unless it is coloured otherwise: amber for a queue
 * someone has to work through, orange and breathing for work still moving, and plain for a tally
 * that is merely a fact about what is on the tab — "3 items read" asks nothing of anyone.
 */
type TabTone = 'waiting' | 'live' | 'neutral';

function TabButton({
  active, onClick, label, count, tone = 'waiting',
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number | null;
  tone?: TabTone;
}) {
  const live = tone === 'live';
  return (
    <button
      onClick={onClick}
      className={cn(
        'relative -mb-px flex items-center gap-1.5 px-0.5 pb-2 text-xs font-medium border-b-2 transition-colors',
        active
          ? 'border-orange-500 text-ink'
          : 'border-transparent text-ink-muted hover:text-ink',
      )}
    >
      {label}
      {count !== null && count > 0 && (
        <span
          className={cn(
            'text-[9px] font-semibold px-1.5 py-0.5 rounded-full inline-flex items-center gap-1',
            tone === 'live' && 'bg-orange-100 dark:bg-orange-950/60 text-orange-700 dark:text-orange-400',
            tone === 'waiting' && 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400',
            tone === 'neutral' && 'bg-surface-muted text-ink-muted',
          )}
        >
          {live && <BreathingDot size={6} />}
          {count}
        </span>
      )}
    </button>
  );
}
